import { useCallback, useContext } from 'react'
import * as React from 'react'

import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import noop from 'lib/function/noop'
import { getCurrentShortOccupancyLabel, getIsOccupancyMissingChildAges } from 'selectors/offerPage/offerPageSelectors'

import OfferFilterSelectChip from '../OfferFilterSelectChip'

interface Props {
  occupancyLabel: string
  occupancyInvalid: boolean
  isOpen: boolean
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void
  className?: string
  ref?: React.Ref<HTMLButtonElement>
}

/**
 * Chip trigger for the occupancy filter dropdown — the `chip` trigger variant of
 * `OfferCapacityWithAgesSelect`. It carries the short occupancy label the offer page's
 * mobile filter row uses ("2 adults · 1 room"), rather than the labelled value of
 * `OfferCapacityWithAgesSelectBox`.
 */
function OfferCapacitySelectChip(props: Props) {
  const { occupancyLabel, occupancyInvalid, isOpen, onClick = noop, className, ref } = props
  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)

  const onChipClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    if (isOpen) {
      dispatch({ type: OfferPageActions.closeFilter })
      trackEvent('OCCUPANCY_FILTERS_CLOSED')
    } else {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
      trackEvent('OCCUPANCY_FILTERS_PRESSED')
      onClick(e)
    }
  }, [dispatch, isOpen, onClick, trackEvent])

  return (
    <OfferFilterSelectChip
      ref={ref}
      className={className}
      size="medium"
      selected={isOpen}
      error={occupancyInvalid}
      startIcon={<LineUsersAltIcon />}
      onClick={onChipClick}
      data-testid="OfferCapacitySelectBox"
    >
      {occupancyLabel}
    </OfferFilterSelectChip>
  )
}

type MappedProps = Pick<Props, 'occupancyLabel' | 'occupancyInvalid' | 'isOpen'>

export default withOfferPageState((state): MappedProps => ({
  occupancyLabel: getCurrentShortOccupancyLabel(state),
  occupancyInvalid: getIsOccupancyMissingChildAges(state),
  isOpen: state.activeFilter === 'capacity',
}), OfferCapacitySelectChip)
